import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  setToken,
  getToken,
  cleanToken,
  setUser,
  getUser,
  cleanUser,
} from '../shared/localStoreage'
import jwtDecode from 'jwt-decode'
import HttpModule from 'src/shared/http'
import { ALTERAR_SENHA, DASHBOARD, LOGIN } from 'src/shared/pathRoutes'

const AuthContext = createContext({})

const AuthProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const toLogin = () => navigate(LOGIN)
  const toDashboard = () => navigate(DASHBOARD)
  const toChangePassword = (search = '') => navigate(`${ALTERAR_SENHA}${search}`)
  const [authenticated, setAuthenticated] = useState(false)

  const handleAuthenticated = () => {
    const token = getToken()
    const user = getUser()
    if (token && user) {
      const userWithDefaults = {
        ...user,
        pricePerCredit: user.pricePerCredit ?? 2,
      }
      setUser(userWithDefaults)
      setAuthenticated(true)
    } else {
      setAuthenticated(false)
    }
  }

  const [data, setData] = useState(() => {
    const user = getUser()
    if (user) {
      return {
        user: {
          ...user,
          pricePerCredit: user.pricePerCredit ?? 2,
        },
      }
    }
    return {}
  })

  useEffect(() => {
    handleAuthenticated()
  }, [])

  useEffect(() => {
    if (location?.pathname === ALTERAR_SENHA) {
      toChangePassword(location?.search)
      return
    }
    if (authenticated) {
      toDashboard()
      return
    }
    if (location?.pathname === LOGIN) {
      return
    }
    toLogin()
  }, [authenticated])

  const signIn = useCallback(async (username, password) => {
    const httpModule = new HttpModule()
    httpModule.setResource('/signin')
    httpModule.setBody({ username, password })
    const response = await httpModule.post()
    const { accessToken } = response.data
    const tokenDecoded = jwtDecode(accessToken)
    console.log('tokenDecoded', tokenDecoded)
    const userWithDefaults = {
      ...tokenDecoded,
      pricePerCredit: tokenDecoded.pricePerCredit ?? 2,
    }
    setToken(accessToken)
    setUser(userWithDefaults)
    setData({ user: userWithDefaults })
    handleAuthenticated()
  }, [])

  const signOut = useCallback(() => {
    cleanToken()
    cleanUser()
    setData({})
    toLogin()
    handleAuthenticated()
  }, [])

  const forgotPassword = useCallback(async (email) => {
    const httpModule = new HttpModule()
    httpModule.setResource('/password/recovery')
    httpModule.setBody({ identifier: email })
    await httpModule.post()
    toLogin()
  }, [])

  const changePassword = useCallback(async (payload) => {
    const { hash, password, confirmPassword } = payload
    const httpModule = new HttpModule()
    httpModule.setResource('/password/recovery')
    httpModule.setBody({ hash, newPassword: password, confirmNewPassword: confirmPassword })
    await httpModule.put()
    toLogin()
  }, [])

  const refreshMenu = useCallback(async (menu) => {
    setData((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        menu: menu,
      },
    }))
  }, [])

  const refreshUser = (payload) => {
    const { name, email, preferences } = payload
    const { timeZone } = preferences
    setData((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        name: name,
        email: email,
        preferences: {
          ...prev.user.preferences,
          timeZone: timeZone,
        },
      },
    }))
  }

  return (
    <AuthContext.Provider
      value={{
        user: data,
        signIn,
        signOut,
        refreshMenu,
        refreshUser,
        forgotPassword,
        changePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export { AuthProvider, useAuth }
